import * as Lockr from 'lockr';
import type { IMouseEvent } from '@babylonjs/core/Events/deviceInputEvents';
import { Observer } from '@babylonjs/core/Misc/observable';
import { PointerInfo, PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';

import App from '../App';
import { Person } from './Person';

import { ListenableEvent } from '@egr/wcf/modules/utils';
import { Camera } from '@egr/wcf/modules/core/view';

interface KeyboardMapping {
    forward: Array<string>;
    backward: Array<string>;
    left: Array<string>;
    right: Array<string>;
    lookUp: Array<string>;
    lookDown: Array<string>;
    lookLeft: Array<string>;
    lookRight: Array<string>;
    lookAhead: Array<string>;
    toggleHeight: Array<string>;
}

export class KeyboardControl {
    public readonly eventHeightChanged: ListenableEvent<void, KeyboardControl>;
    public mouseControl: MouseControl;
    private keyboardMapping: KeyboardMapping;
    private actions: Array<string> = [];
    private gameLoop: number;

    public constructor(public readonly app: App) {
        this.eventHeightChanged = new ListenableEvent<void, KeyboardControl>(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.checkInputs = this.checkInputs.bind(this);

        const keyboardMapping: KeyboardMapping = Lockr.get<KeyboardMapping>('keyboardMapping');
        if (keyboardMapping != null) {
            this.keyboardMapping = keyboardMapping;
        } else {
            this.keyboardMapping = {
                forward: ['KeyW'],
                backward: ['KeyS'],
                left: ['KeyA'],
                right: ['KeyD'],
                lookUp: ['ArrowUp'],
                lookDown: ['ArrowDown'],
                lookLeft: ['ArrowLeft'],
                lookRight: ['ArrowRight'],
                lookAhead: ['Digit0'],
                toggleHeight: ['Space']
            };
            Lockr.set('keyboardMapping', this.keyboardMapping);
        }

        this.register();
    }

    public register(): void {
        this.unregister();
        document.addEventListener('keydown', this.onKeyDown, false);
        document.addEventListener('keyup', this.onKeyUp, false);

        this.mouseControl = new MouseControl(this.app);
        setTimeout(this.checkInputs, 33);
    }

    public unregister(): void {
        clearTimeout(this.gameLoop);
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('keyup', this.onKeyUp);

        if (this.mouseControl) {
            this.mouseControl.unregister();
        }
    }

    public setActionKey(action: keyof KeyboardMapping, eventCode: string): void {
        this.removeActionKey(eventCode);
        this.keyboardMapping[action].push(eventCode);
        Lockr.set('keyboardMapping', this.keyboardMapping);
    }

    public removeActionKey(eventCode: string): void {
        const oldAction: keyof KeyboardMapping | undefined = this.getDownKeyAction(eventCode);
        if (oldAction !== undefined) {
            const index: number = this.keyboardMapping[oldAction].indexOf(eventCode);
            if (index > -1) {
                this.keyboardMapping[oldAction].splice(index, 1);
                Lockr.set('keyboardMapping', this.keyboardMapping);
            }
        }
    }

    private onKeyDown(event: KeyboardEvent): void {
        const action: keyof KeyboardMapping | undefined = this.getDownKeyAction(event.code);
        if (action !== undefined && !this.toggleIfOnetimeAction(action)) {
            const index: number = this.actions.indexOf(action);
            if (index === -1) {
                this.actions.push(action);
            }
        }
    }

    private onKeyUp(event: KeyboardEvent): void {
        const action: keyof KeyboardMapping | undefined = this.getDownKeyAction(event.code);
        if (action !== undefined) {
            const index: number = this.actions.indexOf(action);
            if (index > -1) {
                this.actions.splice(index, 1);
            }
        }
    }

    private getDownKeyAction(eventCode: string): keyof KeyboardMapping | undefined {
        let foundAction: keyof KeyboardMapping | undefined;
        for (const action of Object.keys(this.keyboardMapping) as unknown as keyof KeyboardMapping) {
            foundAction = this.keyboardMapping[action].find((value: string, index: number, obj: Array<string>) => {
                return value === eventCode;
            }) as keyof KeyboardMapping;

            if (foundAction !== undefined) {
                return action as keyof KeyboardMapping;
            }
        }
        return undefined;
    }

    private toggleIfOnetimeAction(action: keyof KeyboardMapping): boolean {
        switch (action) {
            case 'toggleHeight':
                Person.toggleHeight(this.app.coreApp.viewer.view);
                this.eventHeightChanged.trigger();
                return true;
            case 'lookAhead':
                Person.lookAhead(this.app.coreApp.viewer.view);
                return true;
            default:
                return false;
        }
    }

    private checkInputs(): void {
        for (const action of this.actions) {
            // move
            if (action === 'forward') {
                Person.moveSimple(this.app.coreApp.viewer.view, 'front', 0.2);
            }

            if (action === 'backward') {
                Person.moveSimple(this.app.coreApp.viewer.view, 'back', 0.2);
            }

            if (action === 'left') {
                Person.moveSimple(this.app.coreApp.viewer.view, 'left', 0.2);
            }

            if (action === 'right') {
                Person.moveSimple(this.app.coreApp.viewer.view, 'right', 0.2);
            }

            // rotate
            if (action === 'lookUp') {
                Person.rotate(this.app.coreApp.viewer.view, 'top', 0.02);
            }

            if (action === 'lookDown') {
                Person.rotate(this.app.coreApp.viewer.view, 'down', 0.02);
            }

            if (action === 'lookLeft') {
                Person.rotate(this.app.coreApp.viewer.view, 'left', 0.02);
            }

            if (action === 'lookRight') {
                Person.rotate(this.app.coreApp.viewer.view, 'right', 0.02);
            }
            this.app.coreApp.viewer.requestRenderFrame();
        }
        this.gameLoop = window.setTimeout(this.checkInputs, 33);
    }
}

class MouseControl {
    public readonly app: App;
    private touchEnabled: boolean;
    private buttons: Array<number>;
    private angularSensibility: number;
    private previousPosition: {x: number, y: number} | null;
    private observer: Observer<PointerInfo> | null;

    constructor(app: App, touchEnabled: boolean = false) {
        this.app = app;
        this.touchEnabled = touchEnabled;
        this.buttons = [0, 1, 2];
        this.angularSensibility = 500.0;

        this.register();
    }

    public register = (): void => {
        this.unregister();
        const camera: Camera = this.app.coreApp.viewer.view.camera;
        this.observer = camera.getScene().onPointerObservable.add(
            this.pointerInput,
            PointerEventTypes.POINTERDOWN | PointerEventTypes.POINTERUP | PointerEventTypes.POINTERMOVE
        );
    };

    public unregister = (): void => {
        if (this.observer) {
            const camera: Camera = this.app.coreApp.viewer.view.camera;
            camera.getScene().onPointerObservable.remove(this.observer);
            this.previousPosition = null;
        }
    };

    // private pointerInput = (pointer: BABYLON.PointerInfo, state: EventState): void => {
    private pointerInput = (pointer: PointerInfo): void => {
        const event: IMouseEvent = pointer.event;

        if (!this.touchEnabled && (event instanceof PointerEvent && event.pointerType === 'touch')) {
            return;
        }
        if (pointer.type !== PointerEventTypes.POINTERMOVE && this.buttons.indexOf(event.button) === -1) {
            return;
        }
        if (pointer.type === PointerEventTypes.POINTERDOWN) {
            this.previousPosition = {
                x: event.clientX,
                y: event.clientY
            };
            event.preventDefault();
        } else if (pointer.type === PointerEventTypes.POINTERUP) {
            this.previousPosition = null;
            event.preventDefault();
        } else if (pointer.type === PointerEventTypes.POINTERMOVE) {
            if (!this.previousPosition) {
                return;
            }

            const offsetX: number = event.clientX - this.previousPosition.x;
            const offsetY: number = event.clientY - this.previousPosition.y;

            const speedX: number = offsetX / this.angularSensibility;
            const speedY: number = offsetY / this.angularSensibility;

            if (offsetX > 0) {
                Person.rotate(this.app.coreApp.viewer.view, 'right', speedX);
            } else if (offsetX < 0) {
                Person.rotate(this.app.coreApp.viewer.view, 'left', -speedX);
            }
            if (offsetY > 0) {
                Person.rotate(this.app.coreApp.viewer.view, 'down', speedY);
            } else if (offsetY < 0) {
                Person.rotate(this.app.coreApp.viewer.view, 'top', -speedY);
            }
            this.previousPosition = {
                x: event.clientX,
                y: event.clientY
            };
            event.preventDefault();
        }
    };
}